import { Copy, Package, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useCatalogProduct } from "@/hooks/use-wholesale";
import { fmtKm } from "@/lib/wholesale-format";
import { cn } from "@/lib/utils";
import type { ItemSnapshot } from "@/services/wholesale-catalog";

export type CatalogSelection = { storeId: string; wcProductId: number; wcVariationId: number | null };

export async function copySku(sku: string) {
  if (!sku) {
    toast.error("Artikal nema SKU");
    return;
  }
  try {
    await navigator.clipboard.writeText(sku);
    toast.success(`SKU ${sku} kopiran`);
  } catch {
    toast.error(`Kopiranje nije uspjelo, SKU je ${sku}`);
  }
}

/** WooCommerce status zalihe u riječ koju veletrgovac koristi. */
const STOCK_STATUS: Record<string, string> = {
  instock: "Na zalihi",
  outofstock: "Nedostupno",
  onbackorder: "Po narudžbi",
};

function stockText(qty: number | null | undefined): string {
  if (qty === null || qty === undefined) return "—";
  return `${qty} kom`;
}

/** Zalihu treba pročitati bez razmišljanja: nula je uvijek crvena, sve ostalo mirno. */
function stockClass(qty: number | null | undefined): string {
  return (qty ?? 0) <= 0 ? "text-destructive" : "";
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 p-2 ring-1 ring-foreground/10">
      <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
      <span className="text-xs tabular-nums">{children}</span>
    </div>
  );
}

/** Detalj artikla iz kataloga: slika, šifra i — za varijabilan proizvod — sve varijacije
 *  sa svojom zalihom i cijenom, jer se veleprodaja dogovara po veličini, ne po proizvodu. */
export function CatalogItemSheet({
  selection,
  onOpenChange,
}: {
  selection: CatalogSelection | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, error } = useCatalogProduct(selection?.storeId, selection?.wcProductId);
  const variations = data?.variations ?? [];

  return (
    <Sheet open={!!selection} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col gap-0 p-0">
        <SheetHeader className="p-4 pb-3 border-b">
          <SheetTitle className="text-sm leading-snug">{data?.name || "Artikal"}</SheetTitle>
          <SheetDescription className="text-[11px]">
            {data ? `${data.storeName} · ${data.type === "variable" ? "varijabilan" : "jednostavan"} artikal` : "Učitavanje detalja"}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
          {error ? (
            <p className="text-destructive text-xs">{(error as Error).message}</p>
          ) : isLoading || !data ? (
            <>
              <Skeleton className="aspect-square w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-24 w-full" />
            </>
          ) : (
            <>
              {data.imageUrl ? (
                <img
                  src={data.imageUrl}
                  alt=""
                  className="w-full aspect-square object-cover ring-1 ring-foreground/10"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-32 bg-muted ring-1 ring-foreground/10 flex flex-col items-center justify-center gap-1.5">
                  <Package className="h-6 w-6 text-muted-foreground/40" />
                  <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Bez slike</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => copySku(data.sku)}
                className="group flex items-center justify-between gap-2 p-2 ring-1 ring-foreground/10 text-left hover:bg-muted/60 transition-colors"
              >
                <span className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Šifra artikla</span>
                  <span className="text-sm tabular-nums truncate">{data.sku || "bez SKU"}</span>
                </span>
                <Copy className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
              </button>

              <div className="grid grid-cols-2 gap-2">
                <Field label="Shop">{data.storeName}</Field>
                <Field label="Cijena">{fmtKm(data.listPrice)}</Field>
                <Field label="Zaliha">
                  <span className={stockClass(data.stockQuantity)}>{stockText(data.stockQuantity)}</span>
                </Field>
                <Field label="Status">{STOCK_STATUS[data.stockStatus] || data.stockStatus || "—"}</Field>
              </div>

              {variations.length > 0 && (
                <div className="ring-1 ring-foreground/10">
                  <div className="flex items-center gap-2 px-2 py-1.5 border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                    <span className="flex-1">Varijacija</span>
                    <span className="w-14 text-right">Zaliha</span>
                    <span className="w-20 text-right">Cijena</span>
                    <span className="w-4" />
                  </div>
                  <div className="divide-y">
                    {variations.map((v: ItemSnapshot) => (
                      <button
                        key={v.wcVariationId ?? 0}
                        type="button"
                        onClick={() => copySku(v.sku)}
                        title="Kopiraj SKU varijacije"
                        className={cn(
                          "group w-full flex items-center gap-2 px-2 py-2 text-left text-xs hover:bg-muted/60 transition-colors",
                          v.wcVariationId === selection?.wcVariationId && "bg-muted/70",
                        )}
                      >
                        <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                          <span className="truncate">{v.name}</span>
                          <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground tabular-nums truncate">
                            {v.sku || "bez SKU"}
                          </span>
                        </span>
                        <span className={cn("w-14 text-right tabular-nums", stockClass(v.stockQuantity))}>
                          {stockText(v.stockQuantity)}
                        </span>
                        <span className="w-20 text-right tabular-nums">{fmtKm(v.listPrice)}</span>
                        <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50 group-hover:text-foreground" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {data.type === "variable" && variations.length === 0 && (
                <p className="text-[11px] text-muted-foreground">
                  Varijacije ovog artikla još nisu zrcaljene — pokreni sinhronizaciju shopa da se prikažu.
                </p>
              )}
            </>
          )}
        </div>

        <div className="border-t px-4 py-2 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
          {isLoading && <Loader2 className="h-3 w-3 animate-spin" />}
          Klik na šifru je kopira
        </div>
      </SheetContent>
    </Sheet>
  );
}

export { stockText, stockClass };
